import { useEffect, useId, useRef, useState, type KeyboardEvent } from "react";

import { InlineError } from "./Feedback";
import { Inline, Stack } from "./Layout";
import { ProviderLogo } from "./ProviderLogo";
import { Text } from "./Typography";

export interface ModelSelectOption {
  value: string;
  label: string;
  providerId?: string;
  group: string;
  estimatedCredits?: number | null;
}

export interface ModelSelectProps {
  label: string;
  value: string;
  options: ModelSelectOption[];
  disabled?: boolean;
  onChange: (value: string) => void;
}

export function ModelSelect({
  label,
  value,
  options,
  disabled = false,
  onChange,
}: ModelSelectProps) {
  const id = useId();
  const trigger = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const selected = options.find((option) => option.value === value);
  const groups = [...new Set(options.map((option) => option.group))];
  useEffect(() => {
    if (open) {
      document
        .getElementById(`${id}-option-${String(active)}`)
        ?.scrollIntoView({
          block: "nearest",
        });
    }
  }, [active, id, open]);

  function choose(option: ModelSelectOption) {
    onChange(option.value);
    setOpen(false);
    trigger.current?.focus();
  }

  function keyDown(event: KeyboardEvent<HTMLButtonElement>) {
    if (event.key === "Escape" || event.key === "Tab") {
      if (open && event.key === "Escape") {
        event.preventDefault();
        event.stopPropagation();
      }
      setOpen(false);
      return;
    }
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      const option = options[active];
      if (open && option) choose(option);
      else {
        setActive(
          Math.max(
            0,
            options.findIndex((option) => option.value === value),
          ),
        );
        setOpen(true);
      }
      return;
    }
    if (["ArrowDown", "ArrowUp", "Home", "End"].includes(event.key)) {
      event.preventDefault();
      setOpen(true);
      const step = event.key === "ArrowUp" ? -1 : 1;
      setActive(
        event.key === "Home"
          ? 0
          : event.key === "End"
            ? options.length - 1
            : !open
              ? Math.max(
                  0,
                  options.findIndex((option) => option.value === value),
                )
              : (active + step + options.length) % options.length,
      );
      return;
    }
    if (event.key.length === 1) {
      const match = options.findIndex((option) =>
        option.label.toLowerCase().startsWith(event.key.toLowerCase()),
      );
      if (match >= 0) {
        setActive(match);
        setOpen(true);
      }
    }
  }

  return (
    <div
      className="marker-field marker-model-select"
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) setOpen(false);
      }}
    >
      <label className="marker-field__label" htmlFor={id} id={`${id}-label`}>
        {label}
      </label>
      <button
        aria-activedescendant={
          open ? `${id}-option-${String(active)}` : undefined
        }
        aria-controls={open ? `${id}-options` : undefined}
        aria-disabled={disabled || options.length === 0}
        aria-expanded={open}
        aria-haspopup="listbox"
        aria-labelledby={`${id}-label`}
        className="marker-model-select__trigger"
        disabled={disabled || options.length === 0}
        id={id}
        onClick={() => {
          setActive(
            Math.max(
              0,
              options.findIndex((option) => option.value === value),
            ),
          );
          setOpen(!open);
        }}
        onKeyDown={keyDown}
        ref={trigger}
        role="combobox"
        type="button"
      >
        <Inline>
          {selected ? (
            <ProviderLogo
              label={selected.label}
              providerId={selected.providerId}
            />
          ) : null}
          <Text>{selected?.label ?? "Select a model"}</Text>
        </Inline>
      </button>
      {open ? (
        <div
          aria-labelledby={`${id}-label`}
          className="marker-model-select__options"
          id={`${id}-options`}
          role="listbox"
        >
          {groups.map((group) => (
            <div aria-label={group} key={group} role="group">
              <Text className="marker-model-select__group" variant="caps">
                {group}
              </Text>
              {options
                .filter((option) => option.group === group)
                .map((option) => {
                  const index = options.indexOf(option);
                  return (
                    <button
                      aria-describedby={`${id}-detail-${String(index)}`}
                      aria-label={option.label}
                      aria-selected={option.value === value}
                      className="marker-model-select__option"
                      data-active={index === active}
                      id={`${id}-option-${String(index)}`}
                      key={option.value}
                      onClick={() => {
                        choose(option);
                      }}
                      onMouseEnter={() => {
                        setActive(index);
                      }}
                      onMouseDown={(event) => {
                        event.preventDefault();
                      }}
                      role="option"
                      tabIndex={-1}
                      type="button"
                    >
                      <Inline>
                        <ProviderLogo
                          label={option.label}
                          providerId={option.providerId}
                        />
                        <Stack gap={1}>
                          <Text>{option.label}</Text>
                          <Text
                            id={`${id}-detail-${String(index)}`}
                            variant="muted"
                          >
                            {group}
                            {option.estimatedCredits == null
                              ? ""
                              : ` · ~${String(option.estimatedCredits)} ${option.estimatedCredits === 1 ? "credit" : "credits"} / evaluation`}
                          </Text>
                        </Stack>
                      </Inline>
                    </button>
                  );
                })}
            </div>
          ))}
        </div>
      ) : null}
      {options.length === 0 ? (
        <InlineError>No judge models are configured.</InlineError>
      ) : null}
    </div>
  );
}
